<template>
  <DContextMenu2 ref="contextMenuRef" :model="contextItems" />

  <div v-for="node in filteredNodes" :key="node.key" class="flex flex-col">
    <div v-if="node.sections" class="mt-1">
      <div v-for="section in node.sections" :key="section">
        <SelectedNodeComponent
          :dataType="section.type"
          :nodeType="section.key"
          :module="module"
          :disabled="disabled"
          :excludedKeys="section.exclude"
          @update="handleNestedUpdate" />
      </div>
    </div>

    <div v-else-if="node.columns && isVisible(node)" class="mt-1 flex flex-col">
      <Label :text="node.label" />
      <div class="flex flex-col gap-1">
        <div v-for="column in node.columns" :key="column" class="flex-1">
          <Field
            :field="column"
            :value="getValue(column.key)"
            :module="module"
            :resetCounter="resetCounter"
            :disabled="disabled || (isKeyFramed(column.key) && !isAtKeyframeTime(column.key))"
            @change="(value, isRoot, phase) => handleChange(column.key, value, isRoot, phase)"
            @contextmenu="handleContextMenu"></Field>
          <template v-if="props.module?.type === 'led_screen'">
            <div v-if="column.key === 'horizontalSize' && module.data.tileWidth">
              <small>
                Width:
                {{ formatLength(module.data.tileWidth * (module.data.horizontalSize || 0), 2) }}
              </small>
            </div>
            <div v-if="column.key === 'totalWidth' && module.data.tileWidth > 0">
              <small>
                Horizontal:
                {{ Math.round((module.data.totalWidth || 0) / module.data.tileWidth) }}
              </small>
            </div>
            <div v-if="column.key === 'verticalSize' && module.data.tileHeight">
              <small>
                Height:
                {{ formatLength(module.data.tileHeight * (module.data.verticalSize || 0), 2) }}
              </small>
            </div>
            <div v-if="column.key === 'totalHeight' && module.data.tileHeight > 0">
              <small>
                Vertical:
                {{ Math.round((module.data.totalHeight || 0) / module.data.tileHeight) }}
              </small>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div v-else-if="isVisible(node)" class="mt-1 flex flex-col">
      <Field
        :field="node"
        :value="getValue(node.key)"
        :module="module"
        :resetCounter="resetCounter"
        :disabled="disabled || (isKeyFramed(node.key) && !isAtKeyframeTime(node.key))"
        @change="(value, isRoot, phase) => handleChange(node.key, value, isRoot, phase)"
        @contextmenu="handleContextMenu"></Field>
    </div>
  </div>
</template>

<script setup>
import Label from '@/components/panels/Label.vue';
import { useKeyframes } from '@/composables/useKeyframes';
import { useUnitConversion } from '@/composables/useUnitConversion';
import { settingPanels } from '@/constants/settingPanels';
import { useAssetStore } from '@/stores/api/asset';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { DContextMenu2 } from '@disguise/cloud-ui';
import { computed, ref } from 'vue';
import Field from './Field.vue';

const { formatLength } = useUnitConversion();

const { isKeyFramed, isAtKeyframeTime, getKeyframeValue } = useKeyframes();
const cruxModuleStore = useCruxModuleStore();
const assetStore = useAssetStore();
const selectionStore = useSelectionStore();
const timelinePlayerStore = useTimelinePlayerStore();
const props = defineProps({
  nodeType: {
    type: String,
    default: null,
  },
  dataType: {
    type: String,
    default: null,
  },
  module: {
    type: Object,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  excludedKeys: {
    type: Array,
    default: () => [],
  },
});

const disabled = computed(() => {
  return props.disabled || props.module?.data?.locked;
});

const filteredNodes = computed(() => {
  const nodes = settingPanels[settingType.value] || [];
  // If we have excludedKeys from parent, filter them out
  if (props.excludedKeys && props.excludedKeys.length > 0) {
    const excludeSet = new Set(props.excludedKeys);
    return nodes.filter((node) => !excludeSet.has(node.key));
  }
  return nodes;
});

const isVisible = (node) => {
  if (!node.visible) return true;

  if (typeof node.visible === 'function') {
    return node.visible(props.module?.data || {});
  }

  return true;
};

const settingType = computed(() => {
  if (props.dataType) {
    return props.dataType;
  }
  if (!props.module) return null;
  if (props.module.type == 'geometry') {
    return props.module.data.shape;
  } else {
    return props.module.type;
  }
});

const emit = defineEmits(['update']);

const getValue = (key) => {
  if (!key) return undefined;
  if (!props.module?.data) return undefined;

  if (!props.module) return undefined;
  if (props.module && props.module[key] !== undefined) {
    return props.module[key];
  }
  if (!props.module?.data) return undefined;

  if (isKeyFramed(key)) {
    return getKeyframeValue(key);
  }

  if (!key) return;
  if (!key.includes('.')) {
    return props.module?.data[key];
  }
  const keys = key.split('.');
  let current = props.module?.data;
  for (let i = 0; i < keys.length; i++) {
    if (current == null) return undefined;
    current = current[keys[i]];
  }
  return current;
};

const handleChange = (key, value, isRoot = false, phase) => {
  if (phase) {
    emit('update', { key, value, isRoot, phase });
    return;
  }
  if (isKeyFramed(key)) {
    // update keyframe at current time
    cruxModuleStore.setKeyframeAtCurrent(selectionStore.selectedObjectId, timelinePlayerStore.currentTime, key, value);
    return;
  }
  emit('update', { key, value, isRoot });
  const data = props.module?.data || {};
  if (props.module?.type === 'led_screen') {
    // When totalWidth or totalHeight changes → update tile counts
    const tileWidth = Number(data.tileWidth) || 0;
    const tileHeight = Number(data.tileHeight) || 0;
    if (key === 'totalWidth' || key === 'totalHeight') {
      const totalWidth = key === 'totalWidth' ? value : data.totalWidth || 0;
      const totalHeight = key === 'totalHeight' ? value : data.totalHeight || 0;

      if (tileWidth > 0 && tileHeight > 0) {
        const horizontalSize = Math.max(1, Math.round(totalWidth / tileWidth));
        const verticalSize = Math.max(1, Math.round(totalHeight / tileHeight));

        emit('update', { key: 'horizontalSize', value: horizontalSize });
        emit('update', { key: 'verticalSize', value: verticalSize });
      }
    }

    // When horizontalSize or verticalSize changes → update total dimensions
    if (key === 'horizontalSize' || key === 'verticalSize') {
      const horizontalSize = key === 'horizontalSize' ? value : data.horizontalSize || 0;
      const verticalSize = key === 'verticalSize' ? value : data.verticalSize || 0;

      if (tileWidth > 0 && tileHeight > 0) {
        const totalWidth = tileWidth * horizontalSize;
        const totalHeight = tileHeight * verticalSize;

        emit('update', { key: 'totalWidth', value: totalWidth });
        emit('update', { key: 'totalHeight', value: totalHeight });
      }
    }
    if (key === 'sizeMode') {
      if (value === 'dimensions') {
        const tileWidth = Number(data.tileWidth) || 1;
        const tileHeight = Number(data.tileHeight) || 1;
        const horizontalSize = Number(data.horizontalSize) || 1;
        const verticalSize = Number(data.verticalSize) || 1;
        const totalWidth = tileWidth * horizontalSize;
        const totalHeight = tileHeight * verticalSize;
        emit('update', { key: 'totalWidth', value: totalWidth });
        emit('update', { key: 'totalHeight', value: totalHeight });
      } else if (value === 'tiles') {
        const tileWidth = Number(data.tileWidth) || 1;
        const tileHeight = Number(data.tileHeight) || 1;
        const totalWidth = Number(data.totalWidth) || tileWidth;
        const totalHeight = Number(data.totalHeight) || tileHeight;
        const horizontalSize = Math.round(totalWidth / tileWidth);
        const verticalSize = Math.round(totalHeight / tileHeight);
        emit('update', { key: 'horizontalSize', value: horizontalSize });
        emit('update', { key: 'verticalSize', value: verticalSize });
      }
    }
  }
};

const handleNestedUpdate = (payload) => {
  emit('update', payload);
};

const handleKeyframe = (field) => {
  const keyframeValue = getValue(field.key);
  cruxModuleStore.addKeyFrametoTimelineLayer(
    assetStore.currentAsset.latest_version.code,
    selectionStore.selectedObjectId,
    field.key,
    timelinePlayerStore.currentTime,
    keyframeValue
  );
};

const handleKeyframeDelete = (field) => {
  cruxModuleStore.deleteKeyframeAtTime(selectionStore.selectedObjectId, timelinePlayerStore.currentTime, field.key);
};

const contextMenuRef = ref(null);
const currentField = ref(null);
const resetCounter = ref(0);

const isTransformField = (key) => {
  return key?.startsWith('position.') || key?.startsWith('rotation.') || key?.startsWith('target.');
};

const isFieldLocked = computed(() => {
  if (disabled.value || props.module?.data?.lock) return true;
  if (!currentField.value) return false;
  const field = currentField.value;
  if (typeof field.disabled === 'function') {
    return !field.disabled(props.module?.data || {});
  }
  return false;
});

const contextItems = computed(() => {
  const items = [];

  if (
    currentField.value &&
    (!isFieldLocked.value || currentField.value.key === 'lock') &&
    !isTransformField(currentField.value.key) &&
    currentField.value.default != null
  ) {
    const resolvedDefault =
      typeof currentField.value.default === 'function'
        ? currentField.value.default(props.module?.data || {})
        : currentField.value.default;

    if (resolvedDefault != null) {
      items.push({
        label: 'Reset to Default',
        command: () => {
          if (currentField.value) {
            const key = currentField.value.key;
            handleChange(key, resolvedDefault, !!currentField.value.root);
            resetCounter.value++;
          }
        },
      });
    }
  }
  if (currentField.value && !isFieldLocked.value) {
    if (
      !isKeyFramed(currentField.value.key) ||
      (isKeyFramed(currentField.value.key) && !isAtKeyframeTime(currentField.value.key))
    ) {
      items.push({
        label: 'Add Keyframe',
        command: () => {
          if (currentField.value) {
            handleKeyframe(currentField.value);
          }
        },
      });
    } else {
      items.push({
        label: 'Delete Keyframe',
        command: () => {
          if (currentField.value) {
            // Handle delete keyframe logic here
            handleKeyframeDelete(currentField.value);
          }
        },
      });
    }
  }

  return items;
});

const handleContextMenu = ({ event, field }) => {
  currentField.value = field;
  contextMenuRef.value.show(event);
};
</script>
